import { useState } from 'react';
import { navigate } from '../app/router';
import { Icon, type IconName } from '../components/ui/Icon';
import { Sheet } from '../components/ui/ui';
import { resolveLook } from '../designs/look';
import { canvasToBlob, renderNoteCanvas, shareOrDownload } from '../lockscreen/exportImage';
import { downloadBlob, notesToMarkdown } from '../storage/backup';
import { store, useStore } from '../state/store';
import type { Note } from '../storage/schemas';

function Action({ icon, label, detail, onClick, danger }: { icon: IconName; label: string; detail?: string; onClick: () => void; danger?: boolean }) {
  return (
    <button type="button" className={`action ${danger ? 'is-danger' : ''}`} onClick={onClick}>
      <Icon name={icon} />
      <span>
        {label}
        {detail && <small>{detail}</small>}
      </span>
    </button>
  );
}

function slug(s: string) {
  return s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '').slice(0, 40) || 'note';
}

export function MoreSheet({ open, onClose, note, title }: { open: boolean; onClose: () => void; note: Note; title: string }) {
  const designs = useStore((s) => s.designs);
  const activeId = useStore((s) => s.active.noteId);
  const [tagDraft, setTagDraft] = useState('');
  const go = (r: Parameters<typeof navigate>[0]) => {
    onClose();
    navigate(r);
  };
  const set = (patch: Partial<Note>) => store.updateNote(note.id, (n) => ({ ...n, ...patch }));

  const exportPng = async () => {
    onClose();
    try {
      const canvas = await renderNoteCanvas(note, resolveLook(note, designs));
      const how = await shareOrDownload(await canvasToBlob(canvas), `${slug(title)}.png`);
      if (how === 'downloaded') store.toast('Image saved to your downloads.', 'success');
    } catch (err) {
      store.toast(`Export failed: ${(err as Error).message}`, 'error');
    }
  };

  const addTag = () => {
    const t = tagDraft.trim().replace(/^#/, '').slice(0, 40);
    if (!t || note.tags.includes(t)) return setTagDraft('');
    set({ tags: [...note.tags, t] });
    setTagDraft('');
  };

  return (
    <Sheet open={open} onClose={onClose} title="Note options" tall>
      <div className="more-pane">
        <label className="more-field">
          <span>Name</span>
          <input className="text-input" value={note.title} placeholder={title} maxLength={200} onChange={(e) => set({ title: e.target.value })} aria-label="Note name" />
        </label>
        <div className="more-field">
          <span>Tags</span>
          <div className="tag-row">
            {note.tags.map((t) => (
              <button key={t} type="button" className="chip" onClick={() => set({ tags: note.tags.filter((x) => x !== t) })} aria-label={`Remove tag ${t}`}>
                #{t} ✕
              </button>
            ))}
            <input
              className="text-input tag-input"
              value={tagDraft}
              placeholder="Add tag"
              aria-label="Add tag"
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addTag();
                }
              }}
              onBlur={addTag}
            />
          </div>
        </div>

        <div className="action-list">
          {activeId !== note.id && <Action icon="phone" label="Show on lock screen" onClick={() => (store.setActive(note.id), onClose())} />}
          <Action icon="eye" label="Lock-screen preview" detail="Simulated preview and wallpaper export" onClick={() => go({ name: 'preview' })} />
          <Action icon="alarm" label="Schedules & reminders" onClick={() => go({ name: 'schedules', noteId: note.id })} />
          <Action icon="history" label="Version history" detail="Snapshots of this note" onClick={() => go({ name: 'versions', id: note.id })} />
          <Action icon="pin" label={note.pinned ? 'Unpin' : 'Pin to top of History'} onClick={() => set({ pinned: !note.pinned })} />
          <Action icon="star" label={note.favorite ? 'Remove from favourites' : 'Add to favourites'} onClick={() => set({ favorite: !note.favorite })} />
          <Action
            icon="copy"
            label="Duplicate"
            onClick={() => {
              const copy = store.duplicate(note.id);
              if (copy) go({ name: 'note', id: copy.id });
            }}
          />
          <Action icon="download" label="Export as image (PNG)" onClick={() => void exportPng()} />
          <Action
            icon="share"
            label="Export as Markdown"
            onClick={() => {
              downloadBlob(new Blob([notesToMarkdown([note])], { type: 'text/markdown' }), `${slug(title)}.md`);
              onClose();
            }}
          />
          <Action
            icon="print"
            label="Print"
            onClick={() => {
              onClose();
              setTimeout(() => window.print(), 300);
            }}
          />
          <Action icon="sparkles" label="Templates" onClick={() => go({ name: 'templates' })} />
          <Action icon="timeline" label="Upcoming" onClick={() => go({ name: 'upcoming' })} />
          <Action
            icon="archive"
            label={note.archived ? 'Unarchive' : 'Archive'}
            detail={note.archived ? undefined : 'Hide from the main list without deleting'}
            onClick={() => {
              set({ archived: !note.archived });
              if (!note.archived && activeId === note.id) store.toast('Archived. It stays on the lock screen until you pick another note.');
              onClose();
            }}
          />
          <Action icon="settings" label="Settings" onClick={() => go({ name: 'settings' })} />
        </div>
        <p className="more-meta">
          Created {new Date(note.createdAt).toLocaleString()} · Edited {new Date(note.updatedAt).toLocaleString()}
        </p>
      </div>
    </Sheet>
  );
}
